import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { importAPI, propertiesAPI } from '../lib/api';
import { toast } from 'sonner';
import { Upload, FileText, CheckCircle2, XCircle, Download, Loader2 } from 'lucide-react';

function downloadTemplate(name, headers, exampleRows) {
  const csv = [headers.join(','), ...exampleRows.map((r) => r.join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function ImportSection({ entity, propertyId, properties, onComplete }) {
  // entity: "units" | "tenants"
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [template, setTemplate] = useState(null);

  useEffect(() => {
    const loader = entity === 'units' ? importAPI.unitsTemplate : importAPI.tenantsTemplate;
    loader().then((r) => setTemplate(r.data)).catch(() => {});
  }, [entity]);

  const handleFile = (e) => {
    setFile(e.target.files?.[0] || null);
    setPreview(null);
    setPreviewError(null);
  };

  const runPreview = async () => {
    if (!propertyId) { toast.error('Bitte zuerst eine Immobilie wählen'); return; }
    if (!file) { toast.error('Bitte eine CSV-Datei auswählen'); return; }
    setLoading(true);
    setPreviewError(null);
    try {
      const fn = entity === 'units' ? importAPI.previewUnits : importAPI.previewTenants;
      const r = await fn(propertyId, file);
      setPreview(r.data);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Vorschau fehlgeschlagen';
      setPreviewError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      toast.error(typeof msg === 'string' ? msg : 'Vorschau fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const runCommit = async () => {
    if (!preview) return;
    const validRows = preview.rows.filter((r) => r.valid).map((r) => r.data);
    if (validRows.length === 0) { toast.error('Keine gültigen Zeilen zum Importieren'); return; }
    setImporting(true);
    try {
      const fn = entity === 'units' ? importAPI.commitUnits : importAPI.commitTenants;
      const r = await fn({ property_id: propertyId, rows: validRows });
      toast.success(`${r.data.created} ${entity === 'units' ? 'Einheiten' : 'Mieter'} importiert (${r.data.skipped} übersprungen)`);
      setPreview(null);
      setFile(null);
      onComplete?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import fehlgeschlagen');
    } finally {
      setImporting(false);
    }
  };

  const headers = entity === 'units'
    ? ['name', 'unit_type', 'area', 'floor', 'rooms']
    : ['unit_name', 'name', 'email', 'phone', 'move_in_date', 'monthly_prepayment', 'person_count'];

  return (
    <div className="space-y-5" data-testid={`import-section-${entity}`}>
      {/* Template */}
      {template && (
        <Alert>
          <FileText className="h-4 w-4" />
          <AlertDescription>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span className="text-sm">
                <strong>Spalten:</strong> {template.headers.join(', ')}<br />
                <span className="text-slate-600">{template.notes}</span>
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadTemplate(template.filename, template.headers, template.example_rows)}
                data-testid={`download-template-${entity}`}
              >
                <Download className="h-4 w-4 mr-2" /> CSV-Vorlage
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Upload */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label>CSV-Datei *</Label>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleFile}
                className="block w-full text-sm text-slate-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 mt-1"
                data-testid={`file-input-${entity}`}
              />
              {file && <p className="text-xs text-slate-500 mt-1">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
            </div>
            <div className="flex items-end">
              <Button
                onClick={runPreview}
                disabled={loading || !file || !propertyId}
                className="bg-emerald-600 hover:bg-emerald-700 w-full"
                data-testid={`preview-btn-${entity}`}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Vorschau prüfen
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Persistent inline error (e.g. missing columns) */}
      {previewError && (
        <Alert variant="destructive" data-testid={`preview-error-${entity}`}>
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>CSV konnte nicht verarbeitet werden:</strong> {previewError}
          </AlertDescription>
        </Alert>
      )}

      {/* Preview */}
      {preview && (
        <Card data-testid={`preview-table-${entity}`}>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <CardTitle>Vorschau ({preview.total_rows} Zeilen)</CardTitle>
              <div className="flex gap-2">
                <Badge variant="default" className="bg-emerald-600">{preview.valid_rows} gültig</Badge>
                {preview.invalid_rows > 0 && <Badge variant="destructive">{preview.invalid_rows} ungültig</Badge>}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead></TableHead>
                    <TableHead>Zeile</TableHead>
                    {headers.map((h) => <TableHead key={h}>{h}</TableHead>)}
                    <TableHead>Fehler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.rows.map((r) => (
                    <TableRow key={r.row_number} className={r.valid ? '' : 'bg-red-50'}>
                      <TableCell>
                        {r.valid ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <XCircle className="h-5 w-5 text-red-600" />}
                      </TableCell>
                      <TableCell>{r.row_number}</TableCell>
                      {headers.map((h) => <TableCell key={h} className="text-sm">{r.data[h] || '–'}</TableCell>)}
                      <TableCell className="text-sm text-red-700">
                        {r.errors.length > 0 ? r.errors.join('; ') : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setPreview(null); setFile(null); }}>Abbrechen</Button>
              <Button
                onClick={runCommit}
                disabled={importing || preview.valid_rows === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid={`commit-btn-${entity}`}
              >
                {importing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {preview.valid_rows} gültige Zeilen importieren
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function ImportPage() {
  const [properties, setProperties] = useState([]);
  const [propertyId, setPropertyId] = useState('');

  useEffect(() => {
    propertiesAPI.getAll().then((r) => {
      setProperties(r.data);
      if (r.data.length > 0) setPropertyId(r.data[0].id);
    });
  }, []);

  return (
    <Layout>
      <div className="space-y-6" data-testid="import-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Daten-Import (CSV)
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Importieren Sie Einheiten und Mieter aus CSV-Dateien. Excel-Export → „CSV UTF-8" funktioniert.
            Bestehende Einträge werden automatisch übersprungen.
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <Label>Ziel-Immobilie *</Label>
            <Select value={propertyId} onValueChange={setPropertyId}>
              <SelectTrigger className="w-full sm:w-96 mt-1" data-testid="import-property-select">
                <SelectValue placeholder="Immobilie wählen" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {properties.length === 0 && (
              <p className="text-sm text-amber-600 mt-2">
                Bitte legen Sie zuerst eine Immobilie an, bevor Sie importieren.
              </p>
            )}
          </CardContent>
        </Card>

        <Tabs defaultValue="units">
          <TabsList>
            <TabsTrigger value="units" data-testid="tab-units">Einheiten</TabsTrigger>
            <TabsTrigger value="tenants" data-testid="tab-tenants">Mieter</TabsTrigger>
          </TabsList>
          <TabsContent value="units" className="pt-4">
            <ImportSection entity="units" propertyId={propertyId} properties={properties} />
          </TabsContent>
          <TabsContent value="tenants" className="pt-4">
            <ImportSection entity="tenants" propertyId={propertyId} properties={properties} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
